import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { euroPreciso as eurV, messaggioErrore } from "@/lib/preventivi";
import { apriVersione } from "@/lib/documenti";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import { ChevronRight, FilePlus, FileText, Phone, Search, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/common/PageHeader";
import StrumentiTabs from "@/components/preventivi/StrumentiTabs";
import { useMedia } from "@/hooks/use-media";
import { cn } from "@/lib/utils";
import { conteggio, data as dataCorta, quando } from "@/lib/format";
import { oggiRoma } from "@/lib/soldi";

const PASSO = 20;
const COLONNE = "grid-cols-[minmax(0,2.2fr)_104px_130px_minmax(0,1.5fr)_176px]";
const COLONNE_STATO = "grid-cols-[minmax(0,2.4fr)_130px_150px_minmax(0,1.25fr)_40px]";

const TONO_STATO = { bozza: "neutral", da_rivedere: "warning", approvato: "success", inviato: "brand" };
const NOME_STATO = { bozza: "Bozza", da_rivedere: "Da rivedere", approvato: "Approvato", inviato: "Inviato" };
const BOZZE = new Set(["bozza", "da_rivedere", "approvato"]);

const SCHEDE = [
  { id: "richiamare", label: "Da richiamare" },
  { id: "bozze", label: "Bozze" },
  { id: "inviati", label: "Inviati" },
  { id: "tutti", label: "Tutti" },
];

const nomeCliente = (c) =>
  c ? c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || "—" : null;

const ORE_24 = 24 * 3600 * 1000;
const apertoDi = (a) => (a && a.volte ? a : null);
const ultimaRecente = (a) => Boolean(apertoDi(a)?.ultima) && Date.now() - new Date(a.ultima).getTime() < ORE_24;

/* Le aperture del documento da parte del cliente. «Mai aperto» è uno stato, non un trattino: è proprio su
   quello che il venditore richiama. Le aperture le conta il server (vista `preventivo_aperture`, robot di
   anteprima esclusi) e ognuno vede solo quelle dei preventivi che la RLS gli fa vedere. */
function Aperture({ aperture, richiamo = false }) {
  if (!aperture) return null;
  if (!aperture.pubblicato) {
    return <span className="text-small text-ink-2">Non ancora inviato</span>;
  }
  if (!aperture.volte) {
    return (
      <div className="flex flex-col items-start gap-1">
        <StatusBadge tone="warning" data-testid="aperture-mai">
          Mai aperto
        </StatusBadge>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1" data-testid="aperture-volte">
      <StatusBadge tone={ultimaRecente(aperture) ? "success" : "brand"}>
        Aperto {aperture.volte === 1 ? "una volta" : `${aperture.volte} volte`}
      </StatusBadge>
      {aperture.ultima ? (
        <span className="text-small text-ink-2">
          L&#39;ultima {quando(new Date(aperture.ultima), { conOra: true })}
          {richiamo && ultimaRecente(aperture) ? " · è un buon momento per sentirla" : ""}
        </span>
      ) : null}
    </div>
  );
}

/* Un preventivo fatto fuori (c36) non ha editor: la riga apre il suo file. */
function apriFile(p) {
  if (!p.file_id) return toast.error("Il file di questo preventivo non c'è più.");
  return apriVersione(p.file_id).catch(() => toast.error("Il file non si è aperto: riprova"));
}

const telefonoLink = (r) => (r.telefono_e164 || r.telefono || "").replace(/[^\d+]/g, "");

/* ── Le righe ────────────────────────────────────────────────────────────── */

function TitoloRiga({ p, cliente, sotto }) {
  const titolo = p.titolo || p.numero || "Preventivo";
  const classi = "block truncate text-left font-semibold text-ink-strong hover:underline max-md:py-3";
  const dettaglio = [cliente ? titolo : null, sotto].filter(Boolean).join(" · ");
  return (
    <div className="min-w-0">
      {p.esterno ? (
        <button
          type="button"
          className={classi}
          onClick={(e) => {
            e.stopPropagation();
            apriFile(p);
          }}
          data-testid={`preventivo-apri-file-${p.id}`}
        >
          {cliente || titolo}
        </button>
      ) : (
        <Link to={`/preventivi/preventivatore/${p.id}`} className={classi} onClick={(e) => e.stopPropagation()}>
          {cliente || titolo}
        </Link>
      )}
      <span className="flex min-w-0 items-center gap-2 text-small text-ink-2">
        {p.esterno ? <StatusBadge data-testid={`preventivo-fuori-${p.id}`}>Fatto fuori</StatusBadge> : null}
        <span className="truncate">{dettaglio}</span>
      </span>
    </div>
  );
}

function RigaRichiamo({ r, largo, apri }) {
  const giorniFa = r.giorni >= 21;
  const quanto = r.giorni === 1 ? "ieri" : `${r.giorni} giorni fa`;
  const tel = telefonoLink(r);
  const cliente = r.cliente || "Cliente senza nome";
  const cella = r.esterno ? (
    <span className="text-small text-ink-2">Ricevuto fuori</span>
  ) : (
    <Aperture aperture={r.aperture} richiamo />
  );
  const azioni = (
    <div className={cn("flex items-center gap-2", largo ? "justify-end" : "")}>
      {tel ? (
        <Button asChild size="sm" variant="outline" aria-label={`Chiama ${cliente}`}>
          <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()} data-testid={`richiamo-chiama-${r.id}`}>
            <Phone aria-hidden /> Chiama
          </a>
        </Button>
      ) : null}
      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); apri(r); }} aria-label={`Apri il preventivo di ${cliente}`}>
        Apri
      </Button>
    </div>
  );
  if (!largo) {
    return (
      <li
        data-testid={`preventivo-row-${r.id}`}
        onClick={() => apri(r)}
        className="flex flex-col gap-3 border-b border-line-subtle bg-surface px-4 py-3.5 last:border-b-0"
      >
        <div className="flex items-start justify-between gap-3">
          <TitoloRiga p={r} cliente={cliente} sotto={r.numero ? `Rif. ${r.numero}` : ""} />
          <span className="shrink-0 font-bold tabular-nums text-ink-strong">{r.valore != null ? eurV(r.valore) : "—"}</span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div data-testid={`preventivo-aperture-${r.id}`}>{cella}</div>
          <span className={cn("shrink-0 text-small tabular-nums", giorniFa ? "font-bold text-danger-ink" : "text-ink-2")}>{quanto}</span>
        </div>
        {azioni}
      </li>
    );
  }
  return (
    <div
      role="row"
      data-testid={`preventivo-row-${r.id}`}
      onClick={() => apri(r)}
      className={cn("grid cursor-pointer items-center gap-4 border-b border-line-subtle bg-surface px-4 py-3 transition-colors duration-150 ease-enter last:border-b-0 hover:bg-surface-2", COLONNE)}
    >
      <div role="cell" className="min-w-0">
        <TitoloRiga p={r} cliente={cliente} sotto={r.numero ? `Rif. ${r.numero}` : ""} />
      </div>
      <span role="cell" className="font-bold tabular-nums text-ink-strong">{r.valore != null ? eurV(r.valore) : "—"}</span>
      <span role="cell" className="tabular-nums">
        {dataCorta(new Date(r.inviato_at)).replace(/^\w+ /, "")}
        <span className={cn("block text-small", giorniFa ? "font-bold text-danger-ink" : "text-ink-2")}>{quanto}</span>
      </span>
      <div role="cell" data-testid={`preventivo-aperture-${r.id}`}>{cella}</div>
      <div role="cell">{azioni}</div>
    </div>
  );
}

function RigaLista({ p, nome, largo, apri }) {
  const cella = p.esterno ? (
    <span className="text-small text-ink-2">Ricevuto fuori</span>
  ) : (
    <Aperture aperture={p.aperture} />
  );
  const sotto = `Rif. ordine ${p.riferimento_ordine || "—"}`;
  const data = (
    <span className="tabular-nums text-ink-2">{new Date(p.inviato_at || p.created_at).toLocaleDateString("it-IT")}</span>
  );
  if (!largo) {
    return (
      <li
        data-testid={`preventivo-row-${p.id}`}
        onClick={() => apri(p)}
        className="flex min-h-16 cursor-pointer flex-col gap-2 border-b border-line-subtle bg-surface px-4 py-3.5 last:border-b-0"
      >
        <div className="flex items-start justify-between gap-3">
          <TitoloRiga p={p} cliente={nome} sotto={sotto} />
          <StatusBadge tone={TONO_STATO[p.stato]}>{NOME_STATO[p.stato] || p.stato}</StatusBadge>
        </div>
        <div className="flex items-start justify-between gap-3" data-testid={`preventivo-aperture-${p.id}`}>
          {cella}
          {data}
        </div>
      </li>
    );
  }
  return (
    <div
      role="row"
      data-testid={`preventivo-row-${p.id}`}
      onClick={() => apri(p)}
      className={cn("grid cursor-pointer items-center gap-4 border-b border-line-subtle bg-surface px-4 py-3 transition-colors duration-150 ease-enter last:border-b-0 hover:bg-surface-2", COLONNE_STATO)}
    >
      <div role="cell" className="min-w-0">
        <TitoloRiga p={p} cliente={nome} sotto={sotto} />
      </div>
      <span role="cell">
        <StatusBadge tone={TONO_STATO[p.stato]}>{NOME_STATO[p.stato] || p.stato}</StatusBadge>
      </span>
      <span role="cell">{data}</span>
      <div role="cell" data-testid={`preventivo-aperture-${p.id}`}>{cella}</div>
      <ChevronRight role="cell" className="ml-auto size-4 text-ink-3" aria-hidden />
    </div>
  );
}

/* ── Le schede e i tre riquadri di chi richiamare ───────────────────────── */

function Schede({ scheda, conteggi, onCambia }) {
  return (
    <div role="group" aria-label="Stato" className="-mb-px flex max-w-full gap-1 overflow-x-auto" data-testid="preventivi-schede">
      {SCHEDE.map((s) => {
        const attiva = scheda === s.id;
        const n = conteggi[s.id];
        return (
          <button
            key={s.id}
            type="button"
            aria-pressed={attiva}
            onClick={() => onCambia(s.id)}
            data-testid={`tab-${s.id}`}
            className={cn(
              "inline-flex h-11 shrink-0 items-center gap-2 border-b-2 px-3 transition-colors duration-150 ease-enter",
              "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
              attiva ? "border-brand font-bold text-ink-strong" : "border-transparent font-medium text-ink-2 hover:text-ink",
            )}
          >
            {s.label}
            {n !== undefined ? (
              <span
                className={cn(
                  "inline-flex h-5 min-w-5 items-center justify-center rounded-xs px-1.5 text-caption font-bold tabular-nums",
                  attiva ? "bg-brand text-on-brand" : "bg-surface-3 text-ink-2",
                )}
                data-testid={`conteggio-${s.id}`}
              >
                {n}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

const RIQUADRI = [
  { id: "mai", label: "Mai aperto", detail: "Il link non è stato aperto: forse non è arrivato.", punto: "bg-warning" },
  { id: "aperto", label: "Aperto, nessuna risposta", detail: "L'hanno letto: è il momento di chiamare.", punto: "bg-brand" },
  { id: "scade", label: "Scadono entro 7 giorni", detail: "Dopo la scadenza il link non si apre più.", punto: "bg-danger" },
];

function Riquadri({ riquadri, attivo, onScegli }) {
  return (
    <div role="group" aria-label="Filtra chi richiamare" className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0 md:pb-0">
      {RIQUADRI.map((k) => {
        const on = attivo === k.id;
        const { n, v } = riquadri[k.id];
        return (
          <button
            key={k.id}
            type="button"
            aria-pressed={on}
            onClick={() => onScegli(on ? null : k.id)}
            data-testid={`riquadro-${k.id}`}
            className={cn(
              "flex min-h-11 shrink-0 basis-56 flex-col items-start gap-1 rounded-lg border bg-surface p-4 text-left transition-colors duration-150 ease-enter md:basis-auto",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
              on ? "border-2 border-brand bg-brand-subtle" : "border-line hover:bg-surface-2",
            )}
          >
            <span className="flex items-center gap-2 font-semibold text-ink">
              <span className={cn("size-2 rounded-full", k.punto)} aria-hidden />
              {k.label}
            </span>
            <span className="flex items-baseline gap-2">
              <span className="font-figure text-display tabular-nums text-ink-strong">{n}</span>
              <span className="tabular-nums text-ink-2">{v ? eurV(v) : ""}</span>
            </span>
            <span className="text-small text-ink-2 max-md:hidden">{k.detail}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── La pagina ───────────────────────────────────────────────────────────── */

export default function PreventivatoreListPage() {
  const [rows, setRows] = useState(null);
  const [errore, setErrore] = useState(false);
  const [fermi, setFermi] = useState(null); // null = carico · false = non si legge
  const [clienti, setClienti] = useState([]);
  const [scheda, setScheda] = useState("richiamare");
  const [riquadro, setRiquadro] = useState(null);
  const [cerca, setCerca] = useState("");
  const [quanti, setQuanti] = useState(PASSO);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cliente_id: "", titolo: "", riferimento_ordine: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const largo = useMedia("(min-width: 768px)");

  const load = useCallback(() => {
    setErrore(false);
    api
      .get("/preventivatore")
      .then((r) => setRows(r.data || []))
      .catch((e) => {
        setRows([]);
        setErrore(true);
        toast.error(messaggioErrore(e));
      });
    api.get("/crm/preventivi-fermi").then((r) => setFermi(r.data?.righe || [])).catch(() => setFermi(false));
    api.get("/clienti").then((r) => setClienti(r.data || [])).catch(() => {});
  }, []);

  useEffect(load, [load]);
  useEffect(() => setQuanti(PASSO), [scheda, riquadro, cerca]);

  const nomi = useMemo(() => Object.fromEntries(clienti.map((c) => [c.id, nomeCliente(c)])), [clienti]);
  const perId = useMemo(() => Object.fromEntries((rows || []).map((p) => [p.id, p])), [rows]);

  /* Da richiamare = i preventivi fermi da più di 7 giorni (la regola è quella del server, la stessa di
     «Oggi»), arricchiti con le aperture e la scadenza che stanno nell'elenco. */
  const richiamare = useMemo(() => {
    if (!fermi) return [];
    return fermi
      .map((f) => ({ ...(perId[f.id] || {}), ...f, aperture: perId[f.id]?.aperture ?? null }))
      .sort((a, b) => {
        const ma = a.aperture?.pubblicato && !a.aperture.volte ? 0 : 1;
        const mb = b.aperture?.pubblicato && !b.aperture.volte ? 0 : 1;
        return ma - mb || b.giorni - a.giorni;
      });
  }, [fermi, perId]);

  const riquadri = useMemo(() => {
    const entro = (() => {
      const d = new Date(`${oggiRoma()}T12:00:00`);
      d.setDate(d.getDate() + 7);
      return d.toISOString().slice(0, 10);
    })();
    const sono = {
      mai: (r) => r.aperture?.pubblicato && !r.aperture.volte,
      aperto: (r) => r.aperture?.volte > 0,
      scade: (r) => r.valido_fino && r.valido_fino >= oggiRoma() && r.valido_fino <= entro,
    };
    const out = {};
    for (const k of RIQUADRI) {
      const dentro = richiamare.filter(sono[k.id]);
      out[k.id] = { n: dentro.length, v: dentro.reduce((s, r) => s + (Number(r.valore) || 0), 0), fn: sono[k.id] };
    }
    return out;
  }, [richiamare]);

  const inviati = (rows || []).filter((p) => p.stato === "inviato");
  const conteggi = {
    richiamare: fermi === null || fermi === false ? undefined : fermi.length,
    bozze: rows ? rows.filter((p) => BOZZE.has(p.stato)).length : undefined,
    inviati: rows ? inviati.length : undefined,
    tutti: rows ? rows.length : undefined,
  };

  const q = cerca.trim().toLowerCase();
  const corrisponde = (testo) => !q || testo.toLowerCase().includes(q);
  let visibili;
  if (scheda === "richiamare") {
    visibili = richiamare
      .filter((r) => !riquadro || riquadri[riquadro].fn(r))
      .filter((r) => corrisponde(`${r.cliente || ""} ${r.titolo || ""} ${r.numero || ""}`));
  } else {
    const base = (rows || []).filter((p) =>
      scheda === "bozze" ? BOZZE.has(p.stato) : scheda === "inviati" ? p.stato === "inviato" : true,
    );
    visibili = base.filter((p) => corrisponde(`${nomi[p.cliente_id] || ""} ${p.titolo || ""} ${p.numero || ""}`));
  }
  const mostrati = visibili.slice(0, quanti);

  const apri = (p) => (p.esterno ? apriFile(p) : nav(`/preventivi/preventivatore/${p.id}`));

  const submit = async () => {
    if (!form.cliente_id) return toast.error("Seleziona un cliente");
    setBusy(true);
    try {
      const r = await api.post("/preventivatore", {
        cliente_id: form.cliente_id,
        titolo: form.titolo || null,
        riferimento_ordine: form.riferimento_ordine || null,
        livello_ricarico_default: "M5",
      });
      setOpen(false);
      setForm({ cliente_id: "", titolo: "", riferimento_ordine: "" });
      nav(`/preventivi/preventivatore/${r.data.id}`);
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const descrizione =
    rows === null
      ? "Carico i preventivi…"
      : fermi === null || fermi === false
        ? `${conteggio(inviati.length, "inviato", "inviati")}`
        : `${conteggio(inviati.length, "inviato", "inviati")} · ${conteggio(fermi.length, "aspetta una risposta da più di 7 giorni", "aspettano una risposta da più di 7 giorni")}`;

  const nuovo = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-nuovo-preventivo">
          <FilePlus />
          Nuovo preventivo
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="nuovo-preventivo-modal">
        <DialogHeader>
          <DialogTitle>Nuovo preventivo</DialogTitle>
          <DialogDescription>
            Scegli il cliente: il preventivo nasce con i 20 blocchi già in ordine.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-card py-inside">
          <div className="grid gap-inside">
            <Label>Cliente</Label>
            <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
              <SelectTrigger data-testid="select-cliente">
                <SelectValue placeholder="Seleziona…" />
              </SelectTrigger>
              <SelectContent>
                {clienti.length === 0 ? (
                  <div className="px-3 py-2 text-small text-ink-2">Nessun cliente disponibile</div>
                ) : (
                  clienti.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`cliente-${c.id}`}>
                      {nomeCliente(c)}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-inside">
            <Label>Titolo (opzionale)</Label>
            <Input
              value={form.titolo}
              onChange={(e) => setForm({ ...form, titolo: e.target.value })}
              placeholder="es. Cucina soggiorno"
              data-testid="input-titolo"
            />
          </div>
          <div className="grid gap-inside">
            <Label>Riferimento ordine (opzionale)</Label>
            <Input
              value={form.riferimento_ordine}
              onChange={(e) => setForm({ ...form, riferimento_ordine: e.target.value })}
              data-testid="input-riferimento"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Annulla
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-nuovo-preventivo">
            {busy ? "Creazione…" : "Crea preventivo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  let corpo;
  if (rows === null || (scheda === "richiamare" && fermi === null)) {
    corpo = <ListSkeleton rows={6} label="Carico i preventivi…" />;
  } else if (errore) {
    corpo = (
      <div data-testid="preventivi-errore">
        <ErrorState title="I preventivi non si caricano" actions={<Button onClick={load}>Riprova</Button>}>
          È solo una lettura: non si è perso niente. Riprova fra un momento.
        </ErrorState>
      </div>
    );
  } else if (scheda === "richiamare" && fermi === false) {
    corpo = (
      <ErrorState title="Non riesco a dire chi richiamare" actions={<Button onClick={load}>Riprova</Button>}>
        Gli altri elenchi si leggono lo stesso: guarda «Inviati».
      </ErrorState>
    );
  } else if (rows.length === 0) {
    corpo = (
      <EmptyState
        icon={FileText}
        title="Nessun preventivo ancora"
        actions={<Button onClick={() => setOpen(true)}><FilePlus /> Nuovo preventivo</Button>}
      >
        Si parte da un cliente: scegli il cliente e il preventivo nasce già con i 20 blocchi in ordine.
      </EmptyState>
    );
  } else if (visibili.length === 0) {
    if (q) {
      corpo = (
        <EmptyState
          icon={Search}
          title={`Nessun risultato per «${cerca.trim()}»`}
          actions={<Button variant="outline" onClick={() => setCerca("")}>Cancella la ricerca</Button>}
        >
          Cerca per nome del cliente o per titolo del preventivo.
        </EmptyState>
      );
    } else if (scheda === "richiamare" && !riquadro) {
      corpo = (
        <div data-testid="richiamare-vuoto">
          <EmptyState
            icon={CheckCircle2}
            tone="success"
            title="Nessuno da richiamare"
            actions={<Button variant="outline" onClick={() => setScheda("inviati")}>Vedi gli inviati</Button>}
          >
            Nessun preventivo aspetta una risposta da più di 7 giorni.
          </EmptyState>
        </div>
      );
    } else {
      corpo = (
        <EmptyState icon={FileText} title="Qui non c'è niente" actions={riquadro ? <Button variant="outline" onClick={() => setRiquadro(null)}>Togli il filtro</Button> : null}>
          {scheda === "bozze" ? "Nessuna bozza: tutti i preventivi sono stati mandati." : "Nessun preventivo in questo elenco."}
        </EmptyState>
      );
    }
  } else {
    const righe = mostrati.map((p) =>
      scheda === "richiamare" ? (
        <RigaRichiamo key={p.id} r={p} largo={largo} apri={apri} />
      ) : (
        <RigaLista key={p.id} p={p} nome={nomi[p.cliente_id]} largo={largo} apri={apri} />
      ),
    );
    corpo = (
      <div className="flex flex-col gap-3">
        <div className="overflow-hidden rounded-xl border border-line bg-surface" data-testid="preventivi-elenco">
          {largo ? (
            <div role="table" aria-label={SCHEDE.find((s) => s.id === scheda).label}>
              <div role="row" className={cn("grid items-center gap-4 border-b border-line bg-surface-2 px-4 py-2.5 text-small font-semibold text-ink-2", scheda === "richiamare" ? COLONNE : COLONNE_STATO)}>
                <span role="columnheader">Preventivo e cliente</span>
                <span role="columnheader">{scheda === "richiamare" ? "Valore" : "Stato"}</span>
                <span role="columnheader">{scheda === "richiamare" ? "Inviato" : "Data"}</span>
                <span role="columnheader">Il cliente</span>
                <span role="columnheader" className={scheda === "richiamare" ? "text-right" : "sr-only"}>Azioni</span>
              </div>
              <div role="rowgroup">{righe}</div>
            </div>
          ) : (
            <ul>{righe}</ul>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-small text-ink-2">
          <span className="tabular-nums" data-testid="preventivi-piede">
            Mostrati {mostrati.length} di {visibili.length}
            {scheda === "richiamare" ? " · prima chi non l'ha mai aperto, poi da quanto aspetta" : ""}
          </span>
          {visibili.length > mostrati.length ? (
            <Button variant="outline" onClick={() => setQuanti((n) => n + PASSO)} data-testid="preventivi-altri">
              Mostra gli altri
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-preventivatore-list">
      <PageHeader
        title="Preventivi"
        description={descrizione}
        actions={
          <>
            <StrumentiTabs />
            <label className="relative block w-full sm:w-64">
              <span className="sr-only">Cerca</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-2" aria-hidden />
              <Input
                type="search"
                value={cerca}
                onChange={(e) => setCerca(e.target.value)}
                placeholder="Cliente o titolo"
                className="pl-10"
                data-testid="preventivi-cerca"
              />
            </label>
            {nuovo}
          </>
        }
      />
      <div className="border-b border-line">
        <Schede scheda={scheda} conteggi={conteggi} onCambia={(s) => { setScheda(s); setRiquadro(null); }} />
      </div>
      {scheda === "richiamare" && fermi && fermi.length > 0 ? (
        <Riquadri riquadri={riquadri} attivo={riquadro} onScegli={setRiquadro} />
      ) : null}
      {corpo}
    </section>
  );
}
